import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import '../../component-styles/MessageTemplatesSettings.css';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import ConfirmModal from '../utils/ConfirmModal';

export default function MessageTemplatesSettings() {
  const userId = localStorage.getItem('userId');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', body: '' });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', pendingAction: null });

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/settings/${userId}`)
      .then((r) => r.json())
      .then((data) => setTemplates(Array.isArray(data.messageTemplates) ? data.messageTemplates : []))
      .catch(() => notify('Failed to load message templates'))
      .finally(() => setLoading(false));
  }, [userId]);

  async function persist(next) {
    setSaving(true);
    try {
      const token = localStorage.getItem('idToken');
      const res = await fetch(`${API_BASE}/api/settings/${userId}/message-templates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ messageTemplates: next }),
      });
      if (!res.ok) throw new Error('Save failed');
      setTemplates(next);
    } catch (err) {
      notify('Failed to save message templates.');
      throw err;
    } finally {
      setSaving(false);
    }
  }

  function openAdd() {
    setEditingId(null);
    setForm({ name: '', body: '' });
    setShowModal(true);
  }

  function openEdit(tpl) {
    setEditingId(tpl.id);
    setForm({ name: tpl.name, body: tpl.body });
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const name = form.name.trim();
    const body = form.body.trim();
    if (!name || !body) return;

    const next = editingId
      ? templates.map((t) => (t.id === editingId ? { ...t, name, body } : t))
      : [...templates, { id: `mt_${Date.now()}`, name, body }];

    try {
      await persist(next);
      setShowModal(false);
      notify('Message template saved!');
    } catch {
      // persist() already notified the failure
    }
  }

  function handleDelete(tpl) {
    setConfirmModal({
      open: true,
      title: 'Delete Message Template',
      message: `Delete the "${tpl.name}" template? This can't be undone.`,
      pendingAction: async () => {
        try {
          await persist(templates.filter((t) => t.id !== tpl.id));
        } catch {
          // persist() already notified the failure
        }
      },
    });
  }

  async function handleConfirm() {
    const action = confirmModal.pendingAction;
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
    if (action) await action();
  }

  function handleCancel() {
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div className="mtset-container">
      <p className="mtset-desc">
        Save messages you send to clients often — they'll show up as a pick-list when composing an invoice or
        proposal email, so you don't have to retype them each time.
      </p>

      <div className="mtset-header">
        <span className="mtset-section-title">Saved Messages</span>
        <button className="mtset-add-btn" onClick={openAdd}>+ Add Message</button>
      </div>

      <div className="mtset-list">
        {templates.length === 0 && (
          <span className="mtset-empty">No saved messages yet. Add one to get started.</span>
        )}
        {templates.map((tpl) => (
          <div key={tpl.id} className="mtset-card">
            <div className="mtset-card-main">
              <div className="mtset-card-name">{tpl.name}</div>
              <div className="mtset-card-body">{tpl.body}</div>
            </div>
            <div className="mtset-card-actions">
              <button className="mtset-icon-btn" onClick={() => openEdit(tpl)} title="Edit">
                <Pencil size={14} />
              </button>
              <button className="mtset-icon-btn delete" onClick={() => handleDelete(tpl)} title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="mtset-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="mtset-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Edit Message' : 'New Message'}</h3>
            <form className="mtset-form" onSubmit={handleSubmit}>
              <label>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Invoice Follow-up, Welcome Message"
                autoFocus
                required
              />

              <label>Message</label>
              <textarea
                rows={6}
                value={form.body}
                onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                placeholder="e.g. Hi! Please find your invoice attached. Let me know if you have any questions."
                required
              />

              <div className="mtset-actions">
                <button type="button" className="mtset-cancel-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="mtset-save-btn" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="Delete"
        destructive
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
